import $ from 'jquery';
import _ from 'underscore';
import Backbone from 'backbone';
import Handlebars from 'handlebars';
import HandlebarsHelpers from 'handlebarsHelpers';
import BootstrapData from 'models/BootstrapData';
import MultiFetcher from 'MultiFetcher';
import Global from 'models/Global';
import Agenda from 'models/Agenda';
import PollCollection from 'models/PollCollection';
import PartnersCollection from 'models/PartnersCollection';
import SectionsCollection from 'models/SectionsCollection';
import EventCustomization from 'models/EventCustomization';
import MainBaseView from 'views/MainBaseView';
import HomePageView from 'views/HomePageView';
import AgendaView from 'views/AgendaView';
import SessionPageView from 'views/SessionPageView';
import SpeakerPageView from 'views/SpeakerPageView';
import MobileDetect from 'mobileDetect';
import loaderTemplate from 'hbars!templates/loaderView';
import AgendaDefaultDate from 'agendaDefaultDate';
import 'iframe-resizer-content-window';
import { watchResize } from './AppRouterCommons';
import { instanaTrackPage } from '@bizzabo/instana-api';
import FeatureToggleUtils from 'FeatureToggleUtils';
import {
    initializeSnowplowTrackerIfEnabled,
    addSPAccountEventUserToGlobalContext,
    startSPTrackUser
} from '../utils/SnowplowUtils';
import {isBizzaboInternalWebsiteWhiteLabeled} from '@bizzabo/whitelabel-utils';
import { renderVirtualVenueBanner } from '../views/reactViews/VirtualVenueBanner';
import { renderAttendeeLogin } from '../views/reactViews/AttendeeLogin';
import { renderSsoEmailErrorModal } from '../views/reactViews/SsoEmailErrorModal';
import { saveUrlParamsToSessionStorage } from '../utils/saveUrlParamsToSessionStorage';
import { triggerHashScroll } from '../utils/triggerHashScroll';
import { installNonLinkAnchorGuard } from '../utils/nonLinkAnchorGuard';
import {
    parseElementCustomization,
    parseSectionCustomization
} from 'web-common/scripts/utils/sectionsCustomization/parsers';

// SUPP-19669: install as early as possible, before the user can interact with
// any content section (e.g. the registration form's dropdown options).
installNonLinkAnchorGuard();

export const enrichTabSectionsWithCustomizations = (siteSections, { isWidget, dateTimeFormats, ringSettings = {} }) => siteSections.map(sectionModel => {
    try {
        const section = sectionModel.toJSON();
        const sectionFormatDateTime = `${section.type}Element`;
        const parsedSection = parseSectionCustomization(section);
        const formatDateTime = dateTimeFormats[sectionFormatDateTime] || dateTimeFormats.defaultSection;
        const isRingWorks = ringSettings?.isRingWorks || false;
        const elements = (section.elements || []).map(element => {
            element = parseElementCustomization(element);
            const hasChildElements = element?.elements?.length;
            if (hasChildElements) {
                element.elements = element.elements.map(parseElementCustomization);
            }
            return element;
        });

        sectionModel.set({
            ...parsedSection,
            isWidget,
            isRingWorks,
            formatDateTime,
            elements,
        });
    } catch (e) {
        console.warn('Failed to enrich section with customizations', e);
    }
});

const AppRouter = Backbone.Router.extend({
    initialize: function() {
        this.createRoutes([
            [':unique_name(/)', 'tabPage'],
            [':unique_name#', 'tabPage'],
            [':unique_name/home', 'tabPage'],
            [':unique_name/agenda/session/:session', 'session'],
            [':unique_name/agenda/:day', 'agenda'],
            [':unique_name/page/:id(/*path)', 'tabPage'],
            ['*missedRoute', 'missedRoute']
        ]);
        const isWidget = BootstrapData('bootstrap-data-isWidgetObject').isWidget;
        const bootstrapData = {
            event: BootstrapData('bootstrap-data-event'),
            config: BootstrapData('bootstrap-data-config'),
            siteSections: BootstrapData('bootstrap-data-siteSections'),
            siteConf: BootstrapData('bootstrap-data-siteConf'),
            sponsors: BootstrapData('bootstrap-data-sponsors'),
            attendees: BootstrapData('bootstrap-data-attendees'),
            eventCustomization: BootstrapData(
                'bootstrap-data-eventCustomization'
            ),
            isWidget: BootstrapData('bootstrap-data-isWidgetObject').isWidget,
            registrationVersion: BootstrapData(
                'bootstrap-data-registrationVersion'
            ),
            registrationVersionVariant: BootstrapData(
                'bootstrap-data-registrationVersionVariant'
            ),
            isCookieConsentEnabled: BootstrapData('bootstrap-data-isCookieConsentEnabled')
        };
        // DIESEL-183 TEMPORARY (remove when the Backbone content sections are
        // retired — DIESEL-185): the content sections are server-rendered here
        // (React sections carry `renderedHtml`, Backbone ones don't). Tag each
        // section container so QA can locate/identify it in DevTools — search
        // [data-section-type] / [data-section-renderer]; data-* attributes are
        // invisible and don't affect the byte-identical DOM parity.
        try {
            (bootstrapData.siteSections || []).forEach((s) => {
                if (['multiElement', 'html', 'buttons'].includes(s?.type) && s?.rootKey) {
                    const renderer = s?.renderedHtml ? 'react' : 'backbone';
                    const sel = '.section-' + (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(s.rootKey) : s.rootKey);
                    const el = document.querySelector(sel);
                    if (el) {
                        el.setAttribute('data-section-type', s.type);
                        el.setAttribute('data-section-renderer', renderer);
                    }
                }
            });
        } catch (e) { /* diagnostic only */ }
        watchResize();
        FeatureToggleUtils.mergeFeatureToggleWithConfig();
        bootstrapData.eventID = bootstrapData.event.id;
        this.isWidget = bootstrapData.isWidget;
        this.dateTimeFormats = BootstrapData('bootstrap-data-dateTimeFormats');
        this.bodyView = new MainBaseView({
            el: $(document.body),
            bootstrapData: bootstrapData,
        });
        this.siteConf = this.bodyView.siteConf;
        this.loadTemplates();
        this.me = Global.get('me');
        this.event = Global.get('event');
        saveUrlParamsToSessionStorage({
            windowLocationSearch: window?.location?.search,
            eventId: this.event.get('id'),
        });
        this.eventCustomization = new EventCustomization(
            _.isEmpty(bootstrapData.eventCustomization)
                ? {
                    id: this.event.get('id')
                }
                : bootstrapData.eventCustomization
        );
        this.agenda = new Agenda({
            eventId: this.event.get('id'),
            startDate: this.event.get('local-start-date'),
            viewState: this.eventCustomization.get('agenda-view-type')
        });
        this.tabSections = new SectionsCollection();
        this.tabSections.eventId = this.event.get('id');
        this.tabSections.set(
            _.isEmpty(bootstrapData.siteSections)
                ? {}
                : bootstrapData.siteSections,
            {
                parse: true
            }
        );
        Global.set('agenda', this.agenda);
        Global.set('canGoBack', false);
        Global.set('siteConf', this.siteConf);
        Global.set('serverRendered', true);
        const thisRouter = this;
        this.on('route', function(route, params) {
            this.setSeoTitle(params);
            Backbone.trigger('analytics:routeChanged');
            thisRouter.bodyView.sendUrlToWhiteLabelDomain();
            Global.set('serverRendered', false);
        });
        initializeSnowplowTrackerIfEnabled();
        renderVirtualVenueBanner();
        renderAttendeeLogin();
        renderSsoEmailErrorModal();
    },

    setSeoTitle: function(params) {
        const websiteType = this.event.get('websiteType');
        const tabId =
            websiteType === 'whitelabel'
                ? parseInt(params[0], 10)
                : parseInt(params[1], 10);
        const tabs = this.siteConf.get('tabs');
        const tabSection = tabs.findWhere({
            id: tabId
        });
        const homeTab = tabs.findWhere({
            type: 'home'
        });
        const eventName = this.event.get('name');
        const currentTab = this.siteConf.get('currentTab');
        if (tabSection) {
            const title =
                tabSection.get('seoTitle') ||
                `${tabSection.get('name')} | ${eventName}`;
            $('title').text(title);
        } else if (currentTab === 'home') {
            const title =
                homeTab.get('seoTitle') ||
                `${homeTab.get('name')} | ${eventName}`;
            $('title').text(title);
        } else {
            $('title').text(eventName);
        }
    },

    createRoutes: function(routes) {
        const event = BootstrapData('bootstrap-data-event');
        const isWL = isBizzaboInternalWebsiteWhiteLabeled(event);
        const wlPath = event.whitelabelPath;
        const reversedRoutes = [...routes];
        reversedRoutes.reverse();
        _(reversedRoutes).each(([url, methodName]) => {
            if (!isWL) {
                this.route(url, methodName, this[methodName].bind(this));
                return;
            }
            if (url.startsWith(':unique_name')) {
                const uniqueNamePlaceholder = /^:unique_name/;
                const wlPathWithoutLeadingSlash = wlPath.replace(/^\//, '');
                const newUrl = url
                    .replace(uniqueNamePlaceholder, wlPathWithoutLeadingSlash)
                    .replace(/^\//, '');
                this.route(
                    newUrl,
                    methodName,
                    this[methodName].bind(this, null)
                );
            } else {
                this.route(url, methodName, this[methodName].bind(this));
            }
        });
    },
    executedRoutesCount: 0,
    execute: function(callback, args) {
        this.scrollParentIFrameToTop();
        Global.set('canGoBack', this.executedRoutesCount > 0);
        this.executedRoutesCount++;
        callback && callback.apply(this, args);
    },

    scrollParentIFrameToTop: function() {
        if (this.executedRoutesCount > 0 && window.parentIFrame) {
            window.parentIFrame.sendMessage('scrollToTop', '*');
        }
    },

    tabPage: function(unique_name, tabId) {
        tabId = parseInt(tabId, 10);
        const tabs = this.siteConf.get('tabs');
        const tabSection = tabs.findWhere({
            id: tabId
        });
        instanaTrackPage(tabSection?.attributes?.name || 'Home');
        const homeTab = tabs.findWhere({
            type: 'home'
        });
        this.siteConf.set('currentTab', tabId || 'home');
        const models = this.getSections(
            tabId || this.siteConf.getTabIdByType('home')
        );
        const success = _.bind(function() {
            enrichTabSectionsWithCustomizations(this.tabSections, {isWidget: this.isWidget, dateTimeFormats: this.dateTimeFormats, ringSettings: this.siteConf.get('ringSettings')});
            const view = new HomePageView({
                model: this.tabSections,
                siteConf: this.siteConf,
                isMobile: this.isMobile(),
                tabSection: tabSection ? tabSection.toJSON() : null
            });
            this.bodyView.setMainView(view);
            view.render();
        }, this);
        const error = function() {};
        MultiFetcher.fetch(models, success, error);
        this.showLoader();
        this.startSnowplowTracker();
    },
    agenda: async function(unique_name, dayIndex) {
        this.siteConf.set('currentTab', 'agenda');
        const models = this.getAgendaModels();
        const success = _.bind(function() {
            !dayIndex &&
                this.agenda.set(
                    'viewState',
                    this.eventCustomization.get('agenda-view-type')
                );
            const agendaDefaultDate = new AgendaDefaultDate(
                this.event.get('time-zone-id')
            );
            dayIndex = agendaDefaultDate.getDayIndex(
                dayIndex,
                this.event.get('start-date'),
                this.event.get('end-date'),
                this.eventCustomization.get('agendaDefaultDay')
            );
            const day = this.agenda.getDay(
                this.event.get('start-date'),
                dayIndex
            );
            const view = new AgendaView({
                model: new Backbone.Model({
                    agenda: this.agenda,
                    currentDay: day,
                    daysCount: this.agenda.get('day').length - 1
                })
            });
            this.bodyView.setMainView(view);
            view.render();
        }, this);
        const error = function() {};
        MultiFetcher.fetch(models, success, error);
        !dayIndex && this.showLoader();
    },

    session: function(unique_name, sessionId) {
        this.siteConf.set('currentTab', 'agenda');
        const models = this.getAgendaModels();
        this.polls =
            this.polls ||
            new PollCollection({
                eventId: this.event.get('id')
            });
        this.partners =
            this.partners ||
            new PartnersCollection({
                eventId: this.event.get('id')
            });
        const eventCommunityPublic = !(
            this.event.get('privacy') === 'private' ||
            this.event.get('privacy') === 'private-community'
        );
        if (eventCommunityPublic || this.event.isJoined()) {
            !this.polls.fetched && models.push(this.polls);
        }
        !this.partners.fetched && models.push(this.partners);
        const success = _.bind(function() {
            this.polls.setPartners(this.partners);
            const session = this.agenda.getSession(sessionId);
            session.setPolls(this.polls);
            const view = new SessionPageView({
                model: session,
                event: Global.get('event')
            });
            this.bodyView.setMainView(view);
            view.render();
        }, this);
        const error = function() {};
        MultiFetcher.fetch(models, success, error);
        this.showLoader();
    },

    speaker: function(unique_name, speakerId) {
        this.siteConf.set('currentTab', 'agenda');
        const models = this.getAgendaModels();
        const success = _.bind(function() {
            speakerId = parseInt(speakerId, 10);
            const speaker = this.agenda.get('speaker').get(speakerId);
            const view = new SpeakerPageView({
                model: speaker,
                event: this.event
            });
            this.bodyView.setMainView(view);
            view.render();
        }, this);
        const error = function() {};
        MultiFetcher.fetch(models, success, error);
        this.showLoader();
    },

    missedRoute: function(href) {
        if (this.isWidget) {
            href = this.appendQueryString(href, {
                widget: true
            });
        }
        window.location = '/' + href;
    },

    loadTemplates: function() {
        const templates = {};
        const e = this.bodyView.$('[data-type="template"]');
        e.each(function() {
            const $item = $(this);
            const path = $item.attr('data-name');
            const compiledTemplate = Handlebars.compile(
                _.unescape($item.html())
            );
            templates[path] = compiledTemplate;
        });
        Handlebars.sectionsTemplates = templates;
    },

    getAgendaModels: function() {
        const models = [];
        if (!this.event.isJoinRequired()) {
            !this.agenda.get('fetched') && models.push(this.agenda);
            !this.eventCustomization.get('fetched') &&
                models.push(this.eventCustomization);
        }
        return models;
    },

    getSections: function(tabId) {
        const models = [];
        if (!this.event.isJoinRequired()) {
            const sectionsTabId =
                this.tabSections.at(0) && this.tabSections.at(0).get('tabId');
            if (sectionsTabId != tabId) {
                this.tabSections.tabId = tabId;
                this.tabSections.fetched = false;
            }
            !this.tabSections.fetched && models.push(this.tabSections);
        }
        return models;
    },

    loadUrl: function(route) {
        Backbone.history.loadUrl(route);
        this.autoDetectLinks();
    },

    showLoader: function() {
        this.bodyView && this.bodyView.removeMainView();
        const mainViewElem = $('.main-view');
        if (mainViewElem.hasClass('no-loader')) {
            mainViewElem.removeClass('no-loader');
        } else {
            mainViewElem.html(loaderTemplate({}));
        }
    },

    appendQueryString: function(url, queryVars) {
        const firstSeperator = url.indexOf('?') == -1 ? '?' : '&';
        const queryStringParts = _.map(queryVars, function(value, key) {
            return key + '=' + value;
        });
        const queryString = queryStringParts.join('&');
        return url + firstSeperator + queryString;
    },

    autoDetectLinks: function() {
        const thisRouter = this;
        $(document).on('click', 'a', function(evt) {
            const href = $(this).attr('href');
            const target = $(this).attr('target');
            if (!href || target == '_blank') {
                return;
            }
            const { pathname } = window.location;
            const isJavascript = href.indexOf('javascript') === 0;
            const isHttpOrHttps =
                href.indexOf('http:') === 0 || href.indexOf('https:') === 0;
            const isOutboundLink =
                isHttpOrHttps ||
                href.indexOf('data:') === 0 ||
                href.indexOf('mailto:') === 0 ||
                href.indexOf('tel:') === 0;
            if (!isOutboundLink) {
                evt.preventDefault();

                const acsbWidgetAction = evt?.originalEvent?.acsbListenerEvent ||
                    evt?.currentTarget?.classList?.value.includes('acsb');

                if (!isJavascript && !acsbWidgetAction) {
                    if (pathname === '/') {
                        window.history.pushState('home', 'home', '/home');
                    }
                    Backbone.history.navigate(href, true);
                }
                return;
            }
            if (
                thisRouter.event.get('whitelabel-web') &&
                window !== window.top
            ) {
                window.top.location.href = href;
            }
        });
    },

    isMobile: function() {
        const md = new MobileDetect(navigator.userAgent);
        const isMobile = md.mobile() ? true : false;
        Global.set('isMobile', isMobile);
        return isMobile;
    },
    startSnowplowTracker: function() {
        const event = this.event.toJSON();
        const me = this.me.toJSON();
        const shouldCheckForUser = !!me?.getToken?.();
        addSPAccountEventUserToGlobalContext(me, event);
        startSPTrackUser(shouldCheckForUser);
        return this;
    }
});

const router = new AppRouter({});

Backbone.history.start({
    silent: true,
    pushState: history.pushState,
    hashChange: false, // fix the Backbone behaviour for anchor links: do not remove hash in url on page load
});

const isHashScrollEnabled = FeatureToggleUtils.isFeatureEnabled('enableHashScroll');
if (isHashScrollEnabled) {
    window.addEventListener('DOMContentLoaded', triggerHashScroll, { once: true });
}

const route = window.location.pathname.replace(/\/$/, '');
router.loadUrl(route);

export default AppRouter;
